<script setup lang="ts">
import { computed } from 'vue'
import { Activity, CircleUserRound, Crosshair, Gamepad2, History, Leaf, Moon, ScrollText, Settings, ShieldCheck, Sun } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import GameIcon from '@/components/GameIcon.vue'
import { PresetRegistry } from '@/presets/registry'
import { preloadRouteView, type PrimaryRouteName } from '@/router'
import { useAppStore } from '@/stores/app'
import { GAME_ICONS } from '@/ui/game-icons'

const { t } = useI18n()
const app = useAppStore()
const currentPreset = computed(() => PresetRegistry.list().find((preset) => preset.id === app.presetId))
const currentPresetName = computed(() => currentPreset.value ? t(currentPreset.value.nameKey) : app.presetId.toUpperCase())
const items: readonly { to: string; name: PrimaryRouteName; label: string; icon: typeof Activity; mobileSecondary?: boolean }[] = [
  { to: '/', name: 'live', label: 'nav.dashboard', icon: Activity },
  { to: '/calibration', name: 'calibration', label: 'nav.calibration', icon: Crosshair },
  { to: '/history', name: 'history', label: 'nav.history', icon: History },
  { to: '/settings', name: 'settings', label: 'nav.settings', icon: Settings },
  { to: '/changelog', name: 'changelog', label: 'nav.changelog', icon: ScrollText, mobileSecondary: true },
  { to: '/about', name: 'about', label: 'nav.about', icon: CircleUserRound, mobileSecondary: true },
]

function prepareNavigation(name: PrimaryRouteName): void {
  void preloadRouteView(name)
}
</script>

<template>
  <aside class="app-sidebar">
    <RouterLink class="brand" to="/" :aria-label="t('app.name')">
      <span class="brand-mark"><GameIcon :src="GAME_ICONS.mapleLeaf" dimension="1.875rem" eager><Leaf stroke-width="2.2" /></GameIcon></span>
      <span class="brand-copy">
        <strong class="brand-name">{{ t('app.name') }}</strong>
        <small class="brand-subtitle">{{ t('app.tagline') }}</small>
      </span>
    </RouterLink>
    <nav class="nav-list" :aria-label="t('app.primaryNav')">
      <RouterLink v-for="item in items" :key="item.to" :to="item.to" class="nav-item" :class="{ 'mobile-secondary': item.mobileSecondary }" :aria-label="t(item.label)" :title="t(item.label)" @pointerenter="prepareNavigation(item.name)" @pointerdown="prepareNavigation(item.name)" @focus="prepareNavigation(item.name)">
        <span class="nav-icon"><component :is="item.icon" style="width:1.1875rem;height:1.1875rem" stroke-width="1.9" aria-hidden="true" /></span>
        <span>{{ t(item.label) }}</span>
      </RouterLink>
    </nav>
    <div class="sidebar-footer">
      <div class="current-preset" :title="`${t('app.currentPreset')}: ${currentPresetName}`">
        <Gamepad2 style="width:1rem;height:1rem" aria-hidden="true" />
        <span class="footer-copy"><small>{{ t('app.currentPreset') }}</small><strong>{{ currentPresetName }}</strong></span>
      </div>
      <div class="local-badge"><ShieldCheck style="width:1rem;height:1rem" aria-hidden="true" /><span>{{ t('app.localOnly') }}</span></div>
      <button class="theme-toggle" type="button" :title="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" :aria-label="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" @click="app.toggleTheme">
        <Moon v-if="app.theme === 'light'" style="width:1.0625rem;height:1.0625rem" aria-hidden="true" />
        <Sun v-else style="width:1.0625rem;height:1.0625rem" aria-hidden="true" />
        <span>{{ app.theme === 'light' ? t('settings.dark') : t('settings.light') }}</span>
      </button>
    </div>
  </aside>
</template>
